import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Container,
  Paper
} from "@mui/material";
import BasicDetailsForm from "./talent/onboarding/BasicDetailsForm";
import ResumeDetailsForm from "./talent/onboarding/ResumeDetailsForm";
import MediaUploadForm from "./talent/onboarding/MediaUploadForm";

const steps = ["Basic Details", "Resume Details", "Media Upload"];

const TalentOnboardingForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Read userId from localStorage
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (!storedUserId) {
      navigate('/login');
      return;
    }
    
    setUserId(storedUserId);
    setLoading(false);
  }, [navigate]);

  // Set active step based on URL path
  useEffect(() => {
    const path = location.pathname;
    
    if (path.includes('/basic')) {
      setActiveStep(0);
    } else if (path.includes('/resume')) {
      setActiveStep(1);
    } else if (path.includes('/media')) {
      setActiveStep(2);
    } else {
      setActiveStep(0);
    }
  }, [location.pathname]);

  const handleNext = () => {
    const nextStep = activeStep + 1;
    if (nextStep === 1) {
      navigate('/onboarding/resume');
    } else if (nextStep === 2) {
      navigate('/onboarding/media');
    }
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;    
    if (prevStep === 0) {
      navigate('/onboarding/basic');
    } else if (prevStep === 1) {
      navigate('/onboarding/resume');
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const updateFormData = (newData) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const handleComplete = async () => {
    try {
      const response = await fetch(`/api/User/UpdateUserNavigation/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step: 3 })
      });
      localStorage.removeItem("talentOnboardingStep");
      navigate('/myresume');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const getStepComponent = () => {    
    switch (activeStep) {
      case 0:
        return <BasicDetailsForm userId={userId} data={formData} onUpdate={updateFormData} onNext={handleNext} onSkip={handleSkip} />;
      case 1:
        return <ResumeDetailsForm userId={userId} data={formData} onUpdate={updateFormData} onNext={handleNext} onSkip={handleSkip} onBack={handleBack} />;
      case 2:
        return <MediaUploadForm userId={userId} data={formData} onUpdate={updateFormData} onBack={handleBack} onComplete={handleComplete} onSkip={handleComplete} />;
      default:
        console.log("No matching step component found");
        return <div>Invalid step</div>;
    }
  };

  if (loading) {
    return <div>Loading user data...</div>;
  }

  if (!userId) {
    return <div>Redirecting to login...</div>;
  }

  return (
    <Container>
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Talent Profile Onboarding (Step: {activeStep + 1}/3)
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepComponent()}
      </Paper>
    </Container>
  );
};

export default TalentOnboardingForm;